/**
 * @packageDocumentation
 * Implements the animated win popup overlay, formatting messages, syncing with the logo,
 * and dispatching show/hide events.
 *
 * Updated:
 * - Win text is now parented to the Spine modal so it stays centered and moves with the animation.
 * - Uses Figma modal/text rects to compute a pinned offset (textCenter - modalCenter).
 * - Spine animation reliably replays every show.
 *
 * GSAP REMOVED:
 * - No GSAP tweens run on this container or the Spine.
 * - Fades, text punch, and amount counting are implemented via PIXI.Ticker-based tweens.
 *
 * IMPORTANT FIX:
 * - The win animation was “bobbing” vertically because applyFigmaLayout() was computing pivot from
 *   getLocalBounds() while the animation is playing (stars translate => bounds change => pivot shifts).
 * - We now cache the Spine setup-pose bounds once and always use those for pivot/center calculations.
 */

import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { HandleOrientation } from "./global";
import { currencySymbol, ShowCent } from "./server";
import { LogoAnim } from "../app";
import { BottomBar } from "./bottomBar";
import { Bonus } from "./Bonus";
import { SoundManager } from "./sounds";

const shouldEnableSpinFromPopup = () => {
  const autoplay = (globalThis as any)?.autoplay;
  return !autoplay?.isRunningNow?.();
};

export type WinPopupOptions = {
  spineId?: string;
  spineAnimName?: string;
  overlayAlpha?: number;
  closeOnTap?: boolean;
  winPrefix?: string;
  textStyle?: { [key: string]: any };
  width?: number;
  height?: number;
};

export type ShowWinpopupOptions = {
  animName?: string;
  totalAmount?: number;
  prefix?: string;
};

type FigmaCfg = { BASE_W: number; BASE_H: number; ROOT_OFFSET_X: number; ROOT_OFFSET_Y: number };
type FigmaRect = { left: number; top: number; w: number; h: number };

const FIGMA_L: FigmaCfg = { BASE_W: 1921, BASE_H: 1081, ROOT_OFFSET_X: 0, ROOT_OFFSET_Y: 0 };
const FIGMA_P: FigmaCfg = { BASE_W: 1080, BASE_H: 1920, ROOT_OFFSET_X: 0, ROOT_OFFSET_Y: 0 };

// Values provided by you (Figma Dev Mode)
const FIGMA_L_MODAL: FigmaRect = { left: 329, top: -47, w: 1263, h: 1166 };
const FIGMA_L_TEXT: FigmaRect = { left: 728, top: 537, w: 465, h: 105 };

const FIGMA_P_MODAL: FigmaRect = { left: 7, top: 387, w: 1113, h: 1027 };
const FIGMA_P_TEXT: FigmaRect = { left: 328, top: 774, w: 461, h: 206 };

const WIN_TEXT_Y_TWEAK_L = -140;
const WIN_TEXT_Y_TWEAK_P = 0;

// Asset keys (must match your preload)
const TOTAL_WIN_SPINE_SKELETON_KEY = "Total_win_popup_anim_json";
const TOTAL_WIN_SPINE_ATLAS_KEY = "Total_win_popup_anim_atlas";

function getScreenWH() {
  const appAny = (globalThis as any)?.app ?? (globalThis as any)?.__PIXI_APP__;
  const screenW =
    appAny?.renderer?.screen?.width ?? (window.visualViewport?.width ?? window.innerWidth ?? 1920);
  const screenH =
    appAny?.renderer?.screen?.height ?? (window.visualViewport?.height ?? window.innerHeight ?? 1080);
  return { screenW, screenH };
}

function figmaScaleAndOffset(screenW: number, screenH: number, cfg: FigmaCfg) {
  const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);
  const ox = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
  const oy = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;
  return { s, ox, oy };
}

function figmaRectToScreen(rect: FigmaRect, screenW: number, screenH: number, cfg: FigmaCfg) {
  const { s, ox, oy } = figmaScaleAndOffset(screenW, screenH, cfg);
  return {
    x: ox + rect.left * s,
    y: oy + rect.top * s,
    w: rect.w * s,
    h: rect.h * s,
  };
}

function rectCenter(r: { x: number; y: number; w: number; h: number }) {
  return { cx: r.x + r.w / 2, cy: r.y + r.h / 2 };
}

type BoundsBox = { x: number; y: number; w: number; h: number };

export class WinPopup extends PIXI.Container {
  private overlay!: PIXI.Graphics;
  public winPopup_bg_anim!: Spine;
  public winPopupWintext!: PIXI.Text;

  private opts: Required<WinPopupOptions>;
  private content!: PIXI.Container;
  private isBuilt = false;
  public PopupShowtime!: NodeJS.Timeout;

  BonusPage!: Bonus;
  onHidden: () => void = () => {};

  // --- Ticker-based tween system (no GSAP) ---
  private _tweens: Array<() => boolean> = []; // each returns true when finished
  private _tickerBound = false;

  // --- Stable cached bounds (prevents pivot bobbing) ---
  private _setupBounds?: BoundsBox;

  // --- Last computed layout state (for refit text only) ---
  private _lastIsPortrait = false;
  private _lastSpineScale = 1;
  private _lastTextRect?: { w: number; h: number };
  private _lastOffsetScreen?: { x: number; y: number };

  constructor(opts?: WinPopupOptions) {
    super();

    this.opts = {
      spineId: "win_popup_bg",
      // IMPORTANT: Total_win_popup_anim.json only has animation name: "animation"
      spineAnimName: "animation",
      overlayAlpha: 0.6,
      closeOnTap: true,
      winPrefix: "TOTAL WIN",
      textStyle: {
        fontFamily: "PermanentMarker",
        fontSize: 100,
        fontWeight: "900",
        fill: 0xffffff,
        align: "center",
        dropShadow: {
          alpha: 0.8,
          angle: 1,
          blur: 4,
          color: "0x111111",
          distance: 2,
        },
        stroke: {
          color: "black",
          width: 4,
          join: "round",
        },
      },
      // legacy; overlay/layout uses real screen
      width: 1366,
      height: 768,
      ...opts,
    };

    this.visible = false;
    this.alpha = 0;
    this.sortableChildren = true;

    const { screenW, screenH } = getScreenWH();
    this.createWinpopup(screenW, screenH, this.spineFactory);

    this.ensureTicker();

    HandleOrientation(this.PortraitAdjustFun.bind(this), this.landScapeAdjustFun.bind(this));
  }

  private ensureTicker() {
    if (this._tickerBound) return;
    PIXI.Ticker.shared.add(this.tickTweens);
    this._tickerBound = true;
  }

  private unbindTicker() {
    if (!this._tickerBound) return;
    PIXI.Ticker.shared.remove(this.tickTweens);
    this._tickerBound = false;
  }

  private killTweens() {
    this._tweens.length = 0;
  }

  private tickTweens = () => {
    if (this._tweens.length === 0) return;
    this._tweens = this._tweens.filter((fn) => !fn());
  };

  private tweenNumber(opts: {
    from: number;
    to: number;
    durationSec: number;
    ease?: (t: number) => number;
    onUpdate: (v: number) => void;
    onComplete?: () => void;
  }) {
    const from = opts.from;
    const to = opts.to;
    const dur = Math.max(0.0001, opts.durationSec);
    const ease = opts.ease ?? ((t: number) => t);

    let elapsed = 0;

    const step = () => {
      elapsed += PIXI.Ticker.shared.deltaMS / 1000;
      const t = Math.min(1, elapsed / dur);
      const e = ease(t);
      const v = from + (to - from) * e;

      opts.onUpdate(v);

      if (t >= 1) {
        opts.onUpdate(to);
        opts.onComplete?.();
        return true;
      }
      return false;
    };

    this._tweens.push(step);
  }

  private easeOutQuad(t: number) {
    return 1 - (1 - t) * (1 - t);
  }

  private easeOutBack(t: number) {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }

  createWinpopup(
    screenW: number,
    screenH: number,
    spineFactory?: (src: { skeleton: string; atlas: string }, anim: string) => Spine
  ) {
    if (this.isBuilt) return;

    // Dim overlay (resized each layout pass)
    this.overlay = new PIXI.Graphics().rect(0, 0, screenW, screenH).fill(0x000000);
    this.overlay.alpha = this.opts.overlayAlpha;
    this.overlay.eventMode = "static";
    this.overlay.zIndex = 0;
    this.addChild(this.overlay);

    // Content container
    this.content = new PIXI.Container();
    this.content.zIndex = 1;
    this.addChild(this.content);

    if (spineFactory) {
      // Load + create spine. Keep it paused until showWinpopup() restarts it.
      this.winPopup_bg_anim = spineFactory(
        { skeleton: TOTAL_WIN_SPINE_SKELETON_KEY, atlas: TOTAL_WIN_SPINE_ATLAS_KEY },
        this.opts.spineAnimName
      );
      this.winPopup_bg_anim.state.timeScale = 0;
      this.content.addChild(this.winPopup_bg_anim);

      // Cache a stable setup-pose bounds box ONCE (prevents bobbing pivot)
      this.cacheSetupBounds();
    }

    // Win text (IMPORTANT: parented to Spine so it moves with it)
    this.winPopupWintext = new PIXI.Text({
      text: `${this.opts.winPrefix}\n0`,
      style: this.opts.textStyle,
    });
    this.winPopupWintext.anchor.set(0.5);

    if (this.winPopup_bg_anim) {
      (this.winPopup_bg_anim as any).sortableChildren = true;
      this.winPopup_bg_anim.addChild(this.winPopupWintext);
      this.winPopupWintext.zIndex = 999;
    } else {
      this.content.addChild(this.winPopupWintext);
    }

    if (this.opts.closeOnTap) {
      this.eventMode = "static";
      this.cursor = "pointer";
      this.on("pointertap", () => this.hideWinpopup());
    }

    this.isBuilt = true;

    // Initial layout defaults to landscape; orientation handler will correct it.
    this.applyFigmaLayout(false);
  }

  spineFactory(src: { skeleton: string; atlas: string }, anim: string) {
    const spineAnim = Spine.from({ skeleton: src.skeleton, atlas: src.atlas });
    try {
      spineAnim.state.setAnimation(0, anim, false);
    } catch {}
    return spineAnim;
  }

  private cacheSetupBounds() {
    if (!this.winPopup_bg_anim) return;

    try {
      // Ensure setup pose so bounds are stable and not influenced by stars moving
      this.winPopup_bg_anim.state?.clearTracks?.();
      this.winPopup_bg_anim.skeleton?.setToSetupPose?.();
      this.winPopup_bg_anim.update(0);
    } catch {}

    const b = this.winPopup_bg_anim.getLocalBounds();
    const bw = Math.max(1, b.width);
    const bh = Math.max(1, b.height);

    this._setupBounds = { x: b.x, y: b.y, w: bw, h: bh };
  }

  private armHideOnAnimComplete(trackIndex = 0) {
    if (!this.winPopup_bg_anim?.state) return;

    (this.winPopup_bg_anim.state as any).removeListener?.(this as any);

    const listener = {
      complete: (entry: any) => {
        if (entry?.trackIndex !== trackIndex) return;
        clearTimeout(this.PopupShowtime);
        this.hideWinpopup();
      },
    };

    this.winPopup_bg_anim.state.addListener(listener);
    (this as any)._winPopupSpineListener = listener;
  }

  /**
   * Layout rules:
   * - Spine is fit (contain) into modal rect and centered on that rect.
   * - WinText is pinned to Spine's center + offset (FigmaTextCenter - FigmaModalCenter),
   *   converted into Spine-local space.
   * - WinText scale is fitted into its Figma frame (in Spine-local units).
   */
  private applyFigmaLayout(isPortrait: boolean) {
    if (!this.isBuilt) return;

    this._lastIsPortrait = isPortrait;

    const { screenW, screenH } = getScreenWH();

    // Overlay covers full screen
    this.overlay.clear().beginFill(0x000000, 1).drawRect(0, 0, screenW, screenH).endFill();
    this.overlay.alpha = this.opts.overlayAlpha;
    this.overlay.hitArea = new PIXI.Rectangle(0, 0, screenW, screenH);

    const cfg = isPortrait ? FIGMA_P : FIGMA_L;

    const modalRect = figmaRectToScreen(isPortrait ? FIGMA_P_MODAL : FIGMA_L_MODAL, screenW, screenH, cfg);
    const textRect = figmaRectToScreen(isPortrait ? FIGMA_P_TEXT : FIGMA_L_TEXT, screenW, screenH, cfg);

    const modalC = rectCenter(modalRect);
    const textC = rectCenter(textRect);

    // Screen-space offset between desired text center and modal center
    const offsetScreenX = textC.cx - modalC.cx;
    const offsetScreenY = textC.cy - modalC.cy;

    this._lastTextRect = { w: textRect.w, h: textRect.h };
    this._lastOffsetScreen = { x: offsetScreenX, y: offsetScreenY };

    // Place/scale spine into modal rect (contain) — using STABLE cached bounds
    let spineScale = 1;

    if (this.winPopup_bg_anim) {
      const sb = this._setupBounds ?? (() => {
        const b = this.winPopup_bg_anim.getLocalBounds();
        return { x: b.x, y: b.y, w: Math.max(1, b.width), h: Math.max(1, b.height) };
      })();

      spineScale = Math.min(modalRect.w / sb.w, modalRect.h / sb.h);
      this._lastSpineScale = spineScale;

      // Pivot to center of cached bounds (stable)
      this.winPopup_bg_anim.pivot.set(sb.x + sb.w / 2, sb.y + sb.h / 2);
      this.winPopup_bg_anim.position.set(modalC.cx, modalC.cy);
      this.winPopup_bg_anim.scale.set(spineScale);
    }

    // Place + fit text (does not change spine pivot)
    this.refitWinText();

    this.position.set(0, 0);
    this.scale.set(1);
  }

  /**
   * Refit text without recalculating Spine bounds/pivot.
   * Safe to call frequently during number count-up.
   */
  private refitWinText() {
    if (!this.winPopupWintext) return;
    if (!this.winPopup_bg_anim) return;
    if (this.winPopupWintext.parent !== this.winPopup_bg_anim) return;
    if (!this._setupBounds) return;
    if (!this._lastTextRect || !this._lastOffsetScreen) return;

    const spineScale = this._lastSpineScale || 1;

    const localOffsetX = this._lastOffsetScreen.x / spineScale;
    const localOffsetY = this._lastOffsetScreen.y / spineScale;

    // Use the cached setup bounds for the “center” reference
    const scx = this._setupBounds.x + this._setupBounds.w / 2;
    const scy = this._setupBounds.y + this._setupBounds.h / 2;

    this.winPopupWintext.position.set(scx + localOffsetX, scy + localOffsetY);
    this.winPopupWintext.y +=
      (this._lastIsPortrait ? WIN_TEXT_Y_TWEAK_P : WIN_TEXT_Y_TWEAK_L) / spineScale;

    const desiredLocalW = this._lastTextRect.w / spineScale;
    const desiredLocalH = this._lastTextRect.h / spineScale;

    const tb = this.winPopupWintext.getLocalBounds();
    const tw = Math.max(1, tb.width);
    const th = Math.max(1, tb.height);

    const ts = Math.min(desiredLocalW / tw, desiredLocalH / th);
    const clamped = Math.max(0.5, Math.min(1.25, ts));
    this.winPopupWintext.scale.set(clamped);
  }

  showWinpopup(amount: number | string, animOrOptions?: string | ShowWinpopupOptions, callback?: () => void) {
    SoundManager.Win_Popup.play();

    if (!this.isBuilt) {
      console.warn("WinPopup: call createWinpopup() before showWinpopup().");
      return;
    }

    const isPortrait = (globalThis as any)?.isPortrait ?? false;
    this.applyFigmaLayout(!!isPortrait);

    this.onHidden = () => {
      if (LogoAnim.allsuper) {
        LogoAnim.insideBonus = true;
        LogoAnim.StartBut_disableFun?.();
        this.BonusPage.startBonus();
      }
      callback?.();
    };

    const rawDisplay = Number(amount);
    const displayValue = Number.isFinite(rawDisplay) ? rawDisplay : 0;

    let options: ShowWinpopupOptions | undefined;
    let animName: string | undefined;
    if (typeof animOrOptions === "string") {
      animName = animOrOptions;
    } else if (animOrOptions) {
      options = animOrOptions;
      animName = options.animName;
    }

    const totalCandidate = options?.totalAmount;
    const totalValue =
      typeof totalCandidate === "number" && Number.isFinite(totalCandidate) ? totalCandidate : displayValue;

    const prefix = options?.prefix ?? this.opts.winPrefix;
    const totalText = this.formatAmount(totalValue);

    // Restart the spine animation EVERY time
    if (this.winPopup_bg_anim?.state) {
      const playName = animName || this.opts.spineAnimName;
      try {
        this.winPopup_bg_anim.state.timeScale = 1;
        this.winPopup_bg_anim.state.clearTracks();
        this.winPopup_bg_anim.skeleton?.setToSetupPose?.();
        this.winPopup_bg_anim.state.setAnimation(0, playName, false);
      } catch {}
    }

    // Kill any prior tweens
    this.killTweens();

    const setWinText = (val: number) => {
      const formatted = this.formatAmount(val);
      this.winPopupWintext.text = `${prefix}\n${formatted}`;

      // IMPORTANT: do NOT call applyFigmaLayout() here (that can re-pivot based on animated bounds)
      // We only refit the text box (safe to run frequently).
      this.refitWinText();
    };

    // Amount count-up (no GSAP)
    if (displayValue !== 0) {
      setWinText(0);
      this.tweenNumber({
        from: 0,
        to: displayValue,
        durationSec: 1,
        ease: (t) => this.easeOutQuad(t),
        onUpdate: (v) => setWinText(v),
        onComplete: () => setWinText(displayValue),
      });
    } else {
      setWinText(displayValue);
    }

    // Sync other UI
    LogoAnim.totalwinContainer?.setValue(`${totalText}`);
    BottomBar.updateTotalWin(totalValue);

    BottomBar.TotalBalancevalue = BottomBar.TotalBalancevalue + BottomBar.TotalWinvalue;
    BottomBar.updateBalance(BottomBar.TotalBalancevalue);

    this.zIndex = 10;
    this.visible = true;

    this.fadeTo(1, 0.25);

    // Text punch (no GSAP)
    const baseScale = this.winPopupWintext.scale.x;
    this.winPopupWintext.scale.set(baseScale * 0.95);
    this.scaleTween(this.winPopupWintext, baseScale * 1.05, 0.18, () => {
      this.scaleTween(this.winPopupWintext, baseScale, 0.12);
    });

    clearTimeout(this.PopupShowtime);

    this.emit("winpopup:show", { amount: displayValue, totalAmount: totalValue, prefix });

    // Hide when the Spine animation finishes
    this.armHideOnAnimComplete(0);

    // Fallback
    const FALLBACK_MS = 3500;
    this.PopupShowtime = setTimeout(() => {
      clearTimeout(this.PopupShowtime);
      this.hideWinpopup();
    }, FALLBACK_MS);
  }

  hideWinpopup() {
    clearTimeout(this.PopupShowtime);
    this.killTweens();

    const listener = (this as any)._winPopupSpineListener;
    if (listener && this.winPopup_bg_anim?.state?.removeListener) {
      this.winPopup_bg_anim.state.removeListener(listener);
    }
    (this as any)._winPopupSpineListener = null;

    if (shouldEnableSpinFromPopup()) {
      LogoAnim.StartBut_enableFun?.();
    }

    LogoAnim.state?.setAnimation(0, "idle_anim", true);

    if (this.winPopup_bg_anim?.state) {
      try {
        this.winPopup_bg_anim.state.timeScale = 0;
        this.winPopup_bg_anim.state.clearTracks();
      } catch {}
    }

    if (!this.visible) return;

    this.fadeTo(0, 0.2, () => {
      this.visible = false;
      this.emit("winpopup:hidden");
      this.onHidden();
    });
  }

  setAmount(amount: number | string) {
    if (this.winPopupWintext) {
      this.winPopupWintext.text = `${this.opts.winPrefix}\n${amount}`;
      this.refitWinText();
    }
  }

  dispose() {
    this.removeAllListeners();
    this.unbindTicker();
    this.destroy({ children: true });
    this.isBuilt = false;
  }

  private fadeTo(targetAlpha: number, durSec: number, onComplete?: () => void) {
    const from = this.alpha;
    const to = targetAlpha;

    if (durSec <= 0) {
      this.alpha = to;
      onComplete?.();
      return;
    }

    this.tweenNumber({
      from,
      to,
      durationSec: durSec,
      ease: (t) => t,
      onUpdate: (v) => (this.alpha = v),
      onComplete,
    });
  }

  private scaleTween(target: PIXI.Text, to: number, durSec: number, onComplete?: () => void) {
    const from = target.scale.x;

    if (durSec <= 0) {
      target.scale.set(to);
      onComplete?.();
      return;
    }

    this.tweenNumber({
      from,
      to,
      durationSec: durSec,
      ease: (t) => this.easeOutBack(t),
      onUpdate: (v) => target.scale.set(v),
      onComplete,
    });
  }

  private formatAmount(value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    let amountVal = currencySymbol + safeValue.toFixed(2);
    if (ShowCent) {
      if (safeValue < 1 && safeValue > 0) {
        amountVal = safeValue * 100 + "¢";
        if (amountVal.length > 4) {
          amountVal = (safeValue * 100).toFixed(2) + "¢";
        }
      }
    }
    return amountVal;
  }

  PortraitAdjustFun() {
    (globalThis as any).isPortrait = true;
    this.applyFigmaLayout(true);
  }

  landScapeAdjustFun() {
    (globalThis as any).isPortrait = false;
    this.applyFigmaLayout(false);
  }
}
